/**
 * OCR API Service - Frontend HTTP Client
 * ======================================
 * 
 * This module handles all HTTP communication with the OCR backend API.
 * 
 * Key Features:
 * - Axios-based HTTP client with interceptors
 * - Type-safe API calls using TypeScript
 * - Comprehensive error handling and user-friendly messages
 * - File upload with progress tracking
 * - CSV and JSON export functionality
 * - Vendor lookup integration
 * - Feedback submission
 * - Helper utilities for file validation
 * 
 * Configuration:
 * - API_BASE_URL: Set via REACT_APP_API_URL environment variable
 * - Timeout: 300 seconds (5 minutes) for large file processing
 * - Request/Response interceptors for logging
 * 
 * Error Handling:
 * - Maps HTTP status codes to user-friendly messages
 * - Handles network errors gracefully
 * - Provides specific error messages for common issues
 * 
 * Usage:
 * ```typescript
 * // Single file extraction
 * const result = await OCRApiService.extractSingleFile(file, onProgress);
 * 
 * // Batch processing
 * const results = await OCRApiService.extractBatchFiles(files);
 * 
 * // Export to CSV
 * const csvBlob = await OCRApiService.extractAndExportCSV(files);
 * OCRApiService.downloadBlob(csvBlob, 'invoices.csv');
 * ```
 * 
 * @author Your Team
 * @version 1.0.0
 */

import axios, { AxiosResponse, AxiosError } from 'axios';
import { ExtractionResponse, BatchExtractionResponse, InvoiceData, JobResponse } from '../types/invoice';
import { attachSessionSupport } from './session';

// =============================================================================
// API CONFIGURATION
// =============================================================================

/**
 * Base URL for API requests
 * 
 * In Development:
 * - Uses empty string to leverage Create React App's proxy feature
 * - Proxy configured in package.json: "proxy": "http://localhost:8000"
 * - Requests to /api/* are proxied to backend automatically
 * 
 * In Production:
 * - Set REACT_APP_API_URL to your backend URL
 * - Example: REACT_APP_API_URL=https://api.yourdomain.com
 */
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Axios instance with default configuration
 * 
 * Configuration:
 * - baseURL: API endpoint base URL
 * - timeout: 300 seconds (5 minutes) to handle large file uploads and processing
 * 
 * Why 5 minutes?
 * - Large PDF files can take time to upload
 * - OCR processing can be slow for multi-page documents
 * - LLM postprocessing adds additional time
 */
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 300,000ms = 5 minutes
});
// Session cookie on every request, CSRF token on POST/PUT/PATCH/DELETE.
attachSessionSupport(api);

// Exposed for feature-specific pages that need direct axios access
// (e.g. AdminDashboardPage) while still benefiting from the auth interceptors.
export { api };

// =============================================================================
// AXIOS INTERCEPTORS
// =============================================================================

/**
 * Request Interceptor
 * 
 * Adds authentication token to all requests and logs outgoing requests.
 * Useful for tracking API calls during development.
 */
api.interceptors.request.use(
  (config) => {
    // Add authentication token if available
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log(`Response received from ${response.config.url}:`, response.status);
    return response;
  },
  (error: AxiosError) => {
    console.error('API Error:', error.response?.data || error.message);
    
    // Handle 401 Unauthorized - redirect to login
    if (error.response?.status === 401) {
      // Clear auth data
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_token');
      
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

export class OCRApiService {
  // ==========================================================================
  // CORE API METHODS
  // ==========================================================================
  
  /**
   * Check API health status
   * 
   * Verifies that the backend API is running and the OCR service is initialized.
   * Used by the frontend to show connection status in the UI.
   * 
   * @returns Promise resolving to health status object
   * @throws Error if API is not available
   * 
   * Example:
   * ```typescript
   * const health = await OCRApiService.checkHealth();
   * console.log(health.status); // "healthy"
   * ```
   */
  static async checkHealth(): Promise<{ status: string; message: string }> {
    try {
      const response: AxiosResponse = await api.get('/health');
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error);
      throw new Error('API is not available');
    }
  }

  /**
   * Extract data from a single invoice/receipt file
   * 
   * Uploads a file to the backend for OCR processing and returns structured data.
   * 
   * @param file - File to process (PDF or image)
   * @param onUploadProgress - Optional callback for upload progress tracking
   * @returns Promise resolving to extraction response with invoice data
   * @throws Error if extraction fails or network error occurs
   * 
   * Process:
   * 1. Create FormData with file
   * 2. Upload to /extract endpoint
   * 3. Backend processes file with OCR
   * 4. Returns structured invoice data
   * 
   * Example:
   * ```typescript
   * const result = await OCRApiService.extractSingleFile(
   *   file,
   *   (progress) => console.log(`${progress.loaded} / ${progress.total}`)
   * );
   * console.log(result.data.invoice_number);
   * ```
   */
  static async extractSingleFile(
    file: File,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<ExtractionResponse> {
    // Log request details for debugging
    console.log('🔄 OCRApiService.extractSingleFile called');
    console.log('API Base URL:', API_BASE_URL);
    console.log('File to process:', {
      name: file.name,
      size: file.size,
      type: file.type
    });

    // Create FormData and append file
    // FormData is required for multipart/form-data requests
    const formData = new FormData();
    formData.append('file', file);

    try {
      console.log('📡 Making POST request to /extract endpoint...');
      console.log('📦 FormData contents:');
      console.log(`  file: File(name="${file.name}", size=${file.size}, type="${file.type}")`)
      
      const response: AxiosResponse<ExtractionResponse> = await api.post('/extract3', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      });

      console.log('✅ API response received:', response.status);
      console.log('Response data:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Single file extraction failed:', error);
      console.error('Error response:', (error as AxiosError).response);
      console.error('Error config:', (error as AxiosError).config);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Extract data from multiple files
   */
  static async extractBatchFiles(
    files: File[],
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<BatchExtractionResponse> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    try {
      const response: AxiosResponse<BatchExtractionResponse> = await api.post(
        '/extract-batch',
        formData,
        { onUploadProgress }
      );

      return response.data;
    } catch (error) {
      console.error('Batch extraction failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Extract and export to CSV
   */
  static async extractAndExportCSV(
    files: File[],
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<Blob> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    try {
      const response: AxiosResponse<Blob> = await api.post(
        '/extract-and-export-csv',
        formData,
        {
          responseType: 'blob',
          onUploadProgress,
        }
      );

      return response.data;
    } catch (error) {
      console.error('CSV export failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Extract and export to JSON
   */
  static async extractAndExportJSON(
    files: File[],
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<Blob> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    try {
      const response: AxiosResponse<Blob> = await api.post(
        '/extract-and-export-json',
        formData,
        {
          responseType: 'blob',
          onUploadProgress,
        }
      );

      return response.data;
    } catch (error) {
      console.error('JSON export failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Handle API errors consistently
   */
  private static handleApiError(error: AxiosError): Error {
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const data = error.response.data as any;
      
      switch (status) {
        case 400:
          return new Error(data?.detail || 'Invalid request. Please check your file format.');
        case 413:
          return new Error('File too large. Maximum size is 10MB.');
        case 415:
          return new Error('Unsupported file type. Please use PDF, PNG, JPG, or TIFF.');
        case 422:
          return new Error(data?.detail || 'Validation error. Please check your input.');
        case 500:
          return new Error(data?.detail || 'Server error occurred during processing.');
        case 503:
          return new Error('Service temporarily unavailable. Please try again later.');
        default:
          return new Error(data?.detail || `Server error (${status}). Please try again.`);
      }
    } else if (error.request) {
      // Request was made but no response received
      return new Error('No response from server. Please check your connection.');
    } else {
      // Something else happened
      return new Error(error.message || 'An unexpected error occurred.');
    }
  }

  /**
   * Download blob as file
   */
  static downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Import vendors from CSV file (Admin only)
   */
  static async importVendorsCSV(
    file: File,
    fileType?: string,
    customerGroup?: string,
    userId?: number,
    updateExisting: boolean = false
  ): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      // Append file_type if provided
      if (fileType && (fileType === 'vendor_po' || fileType === 'vendor_number')) {
        formData.append('file_type', fileType);
      }
      // Only append customer_group if it's provided and not empty
      if (customerGroup && customerGroup.trim()) {
        formData.append('customer_group', customerGroup);
      }
      // Only append user_id if it's provided and valid
      if (userId && userId > 0) {
        formData.append('user_id', userId.toString());
      }
      formData.append('update_existing', updateExisting.toString());

      const response: AxiosResponse<any> = await api.post(
        '/admin/vendors/import-csv',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Vendor CSV import failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Get list of customer groups (Admin only)
   */
  static async getCustomerGroups(): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.get('/admin/customer-groups');
      return response.data;
    } catch (error) {
      console.error('Get customer groups failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Get list of users (Admin only)
   */
  static async getUsers(customerGroup?: string): Promise<any> {
    try {
      const params = customerGroup ? { customer_group: customerGroup } : {};
      const response: AxiosResponse<any> = await api.get('/admin/users', { params });
      return response.data;
    } catch (error) {
      console.error('Get users failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Delete a user (Admin only)
   */
  static async getReductoUsage(): Promise<{ success: boolean; total_credits: number; total_cost: number }> {
    try {
      const response: AxiosResponse<any> = await api.get('/admin/reducto-usage');
      return response.data;
    } catch (error) {
      console.error('Get Reducto usage failed:', error);
      return { success: false, total_credits: 0, total_cost: 0 };
    }
  }

  static async deleteUser(userId: number): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.delete(`/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Delete user failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  static async setUserRequiresApproval(userId: number, requiresApproval: boolean): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.put(
        `/admin/users/${userId}/requires-approval`,
        null,
        { params: { requires_approval: requiresApproval } },
      );
      return response.data;
    } catch (error) {
      console.error('Set user requires_approval failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  static async setUserCompletePoLine(
    userId: number,
    canCompletePoLine: boolean,
    defaultCompletePoYes: boolean,
  ): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.put(
        `/admin/users/${userId}/complete-po-line`,
        null,
        {
          params: {
            can_complete_po_line: canCompletePoLine,
            default_complete_po_yes: defaultCompletePoYes,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error('Set user complete-po-line failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Get list of vendor imports (Admin only)
   */
  static async getVendorImports(userId?: number): Promise<any> {
    try {
      const params = userId ? { user_id: userId } : {};
      const response: AxiosResponse<any> = await api.get('/admin/vendors/imports', { params });
      return response.data;
    } catch (error) {
      console.error('Get vendor imports failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Delete a vendor import (Admin only)
   */
  static async deleteVendorImport(importId: number): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.delete(`/admin/vendors/imports/${importId}`);
      return response.data;
    } catch (error) {
      console.error('Delete vendor import failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Assign a vendor import to a user (Admin only)
   * Can assign the same file to multiple users
   */
  static async assignVendorImport(importId: number, userId: number): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.post(
        `/admin/vendors/imports/${importId}/assign`,
        { user_id: userId },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Assign vendor import failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Remove a user from a vendor import assignment (Admin only)
   */
  static async removeUserFromVendorImport(importId: number, userId: number): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.delete(
        `/admin/vendors/imports/${importId}/users/${userId}`
      );
      return response.data;
    } catch (error) {
      console.error('Remove user from vendor import failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Format file size for display
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Validate file type
   */
  static validateFileType(file: File): boolean {
    const supportedTypes = [
      'application/pdf',
      'image/png',
      'image/jpeg',
      'image/tiff',
      'image/bmp',
      'image/gif'
    ];
    return supportedTypes.includes(file.type);
  }

  /**
   * Validate file size
   */
  static validateFileSize(file: File, maxSizeMB: number = 10): boolean {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxSizeBytes;
  }

  /**
   * Submit user feedback for OCR extraction quality
   */
  static async submitFeedback(
    fileId: string,
    filename: string,
    feedback: any
  ): Promise<any> {
    const submission = {
      file_id: fileId,
      filename: filename,
      feedback: feedback
    };

    try {
      const response: AxiosResponse = await api.post('/feedback', submission);
      return response.data;
    } catch (error) {
      console.error('Feedback submission failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Get feedback statistics
   */
  static async getFeedbackStats(): Promise<any> {
    try {
      const response: AxiosResponse = await api.get('/feedback/stats/summary');
      return response.data;
    } catch (error) {
      console.error('Get feedback stats failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Export feedback as CSV
   */
  static async exportFeedbackCSV(): Promise<void> {
    try {
      const response: AxiosResponse<Blob> = await api.get('/feedback/export/csv', {
        responseType: 'blob',
      });
      
      this.downloadBlob(response.data, 'ocr_feedback.csv');
    } catch (error) {
      console.error('Feedback CSV export failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Save edited invoice data
   */
  static async saveEditedInvoiceData(
    filename: string,
    invoiceData: any,
    invoiceId?: number
  ): Promise<any> {
    try {
      const response: AxiosResponse = await api.post('/save-invoice-data', {
        filename,
        invoice_data: invoiceData,
        invoice_id: invoiceId,
      });

      return response.data;
    } catch (error) {
      console.error('Failed to save invoice data:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Export invoice data to Keystone CSV format
   */
  static async exportKeystoneCSV(invoices: InvoiceData[]): Promise<Blob> {
    try {
      const response: AxiosResponse<Blob> = await api.post(
        '/export/keystone-csv',
        { invoice_data_list: invoices },
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Keystone CSV export failed:', error);
      // For 422 validation errors, parse the blob response back to JSON
      if (error?.response?.status === 422 && error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        const parsed = JSON.parse(text);
        error.response.data = parsed;
      }
      throw error;
    }
  }

  /**
   * Export invoice data to Keystone TXT format (same structure as CSV, for direct import)
   */
  static async exportKeystoneTXT(invoices: InvoiceData[]): Promise<Blob> {
    try {
      const response: AxiosResponse<Blob> = await api.post(
        '/export/keystone-txt',
        { invoice_data_list: invoices },
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Keystone TXT export failed:', error);
      if (error?.response?.status === 422 && error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        const parsed = JSON.parse(text);
        error.response.data = parsed;
      }
      throw error;
    }
  }

  /**
   * Submit reviewed invoices directly to Keystone's Inbound API. Backend
   * runs the two-step flow (POST invoice JSON, then POST PDF bytes) per
   * invoice. Replaces the download-TXT-then-manually-import flow for
   * customers whose Keystone endpoint is wired up.
   */
  static async submitToKeystone(
    invoices: InvoiceData[],
  ): Promise<{
    success: boolean;
    destination: string;
    submitted: number;
    results: Array<{
      invoice_number: string;
      success: boolean;
      step?: string;
      status_code: number | null;
      result_key?: string;
      job_key?: string;
      pdf_attached?: boolean;
      response?: unknown;
      detail?: string;
    }>;
  }> {
    const response = await api.post(
      '/keystone/submit',
      { invoice_data_list: invoices },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return response.data;
  }

  /**
   * Look up vendor information from Cleveland County database
   */
  static async lookupVendor(companyName: string, minScore: number = 0.8): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.post(
        '/vendor/lookup',
        { 
          company_name: companyName,
          min_score: minScore 
        }
      );

      return response.data;
    } catch (error) {
      console.error('Vendor lookup failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Search for multiple potential vendor matches
   * Accepts either a single company name (string) or array of company names
   */
  static async searchMultipleVendors(companyNames: string | string[]): Promise<any> {
    try {
      // If single string provided, wrap in array
      const namesArray = Array.isArray(companyNames) ? companyNames : [companyNames];
      
      const response: AxiosResponse<any> = await api.post(
        '/vendor/search-multiple',
        { 
          company_names: namesArray
        }
      );

      return response.data;
    } catch (error) {
      console.error('Multiple vendor search failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Lookup PO (Purchase Order) by PO number
   */
  static async lookupPO(poNumber: string, vendorNumber?: string): Promise<any> {
    try {
      const body: Record<string, string> = { po_number: poNumber };
      if (vendorNumber && vendorNumber.trim()) body.vendor_number = vendorNumber.trim();
      const response: AxiosResponse<any> = await api.post('/po/lookup', body);

      return response.data;
    } catch (error) {
      console.error('PO lookup failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Search for POs by PO number (for autocomplete/dropdown)
   */
  static async searchPOs(query: string, limit: number = 10, vendorNumber?: string): Promise<any> {
    try {
      const params: Record<string, any> = { query, limit };
      if (vendorNumber && vendorNumber.trim()) params.vendor_number = vendorNumber.trim();
      const response: AxiosResponse<any> = await api.get('/po/search', { params });

      return response.data;
    } catch (error) {
      console.error('PO search failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Get PO details by PO number
   */
  static async getPODetails(poNumber: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.get(
        `/po/${encodeURIComponent(poNumber)}`
      );

      return response.data;
    } catch (error) {
      console.error('Get PO details failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Search vendors by name (for autocomplete/dropdown)
   */
  static async searchVendors(query: string, limit: number = 10): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.get(
        `/vendor/search`,
        {
          params: {
            query: query,
            limit: limit
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Vendor search failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Get detailed vendor information by vendor number
   */
  static async getVendorDetails(vendorNumber: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.get(
        `/vendor/${encodeURIComponent(vendorNumber)}`
      );

      return response.data;
    } catch (error) {
      console.error('Get vendor details failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  // ==========================================================================
  // AUTHENTICATION METHODS
  // ==========================================================================

  /**
   * Get current authenticated user information
   */
  static async getCurrentUser(): Promise<any> {
    try {
      const response: AxiosResponse = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      console.error('Get current user failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Register a new user
   */
  static async registerUser(userData: {
    username: string;
    email: string;
    password: string;
    full_name?: string;
  }): Promise<any> {
    try {
      const response: AxiosResponse = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      console.error('User registration failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  // ==========================================================================
  // INVOICE MANAGEMENT METHODS
  // ==========================================================================

  /**
   * Get all saved invoices for the current user
   */
  static async getSavedInvoices(skip: number = 0, limit: number = 100): Promise<any> {
    try {
      const response: AxiosResponse = await api.get('/saved-invoices', {
        params: { skip, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Get saved invoices failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Load old invoices for the current user (with full invoice data)
   */
  static async loadOldInvoices(skip: number = 0, limit: number = 100): Promise<any> {
    try {
      const response: AxiosResponse = await api.get('/invoices/load', {
        params: { skip, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Load old invoices failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Delete an invoice and all related data
   */
  static async deleteInvoice(invoiceId: number): Promise<any> {
    try {
      const response: AxiosResponse = await api.delete(`/invoices/${invoiceId}`);
      return response.data;
    } catch (error) {
      console.error('Delete invoice failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Get a specific invoice by ID
   */
  static async getInvoiceById(invoiceId: number): Promise<any> {
    try {
      const response: AxiosResponse = await api.get(`/saved-invoices/${invoiceId}`);
      return response.data;
    } catch (error) {
      console.error('Get invoice by ID failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Get feedback by ID
   */
  static async getFeedbackById(feedbackId: number): Promise<any> {
    try {
      const response: AxiosResponse = await api.get(`/feedback/${feedbackId}`);
      return response.data;
    } catch (error) {
      console.error('Get feedback by ID failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  // ==========================================================================
  // EXTRACTION METHODS (Additional OCR Services)
  // ==========================================================================

  /**
   * Extract data using PaddleOCR (default OCR service)
   */
  static async extractWithPaddleOCR(
    file: File,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<ExtractionResponse> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response: AxiosResponse<ExtractionResponse> = await api.post('/extract', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      });

      return response.data;
    } catch (error) {
      console.error('PaddleOCR extraction failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  /**
   * Extract data using Google Document AI
   */
  static async extractWithDocumentAI(
    file: File,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<ExtractionResponse> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response: AxiosResponse<ExtractionResponse> = await api.post('/extract5', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      });

      return response.data;
    } catch (error) {
      console.error('Document AI extraction failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  // ==========================================================================
  // UTILITY METHODS
  // ==========================================================================

  /**
   * Get list of supported file formats
   */
  static async getSupportedFormats(): Promise<{ supported_formats: string[] }> {
    try {
      const response: AxiosResponse = await api.get('/supported-formats');
      return response.data;
    } catch (error) {
      console.error('Get supported formats failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  static async submitJob(file: File): Promise<JobResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response: AxiosResponse = await api.post('/jobs', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error('Submit job failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }


  static async getJobs(): Promise<{ jobs: JobResponse[]; total: number }> {
    try {
      const response: AxiosResponse = await api.get('/jobs');
      return response.data;
    } catch (error) {
      console.error('Get jobs failed:', error);
      throw this.handleApiError(error as AxiosError);
    }
  }

  // --- Keystone preset-query proxies (live reads from Steve's query.asp) ---

  static async fetchKeystoneVendor(vendorNumber: string): Promise<{ vendor_number: string; data: Record<string, unknown> | null } | null> {
    try {
      const response: AxiosResponse = await api.get(`/keystone-query/vendor/${encodeURIComponent(vendorNumber)}`);
      return response.data;
    } catch {
      // Fail-open: UI falls back to blank defaults when the Keystone side is down.
      return null;
    }
  }

  static async fetchKeystoneCheckCodes(): Promise<Array<{ code: string; desc: string }>> {
    try {
      const response: AxiosResponse = await api.get('/keystone-query/check-codes');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (response.data?.data || []).map((r: any) => ({
        code: String(r?.['CHECK.CODE'] ?? ''),
        desc: String(r?.DESC ?? ''),
      }));
    } catch {
      return [];
    }
  }

  static async fetchKeystoneMyProfile(): Promise<{ user_key: string; data: Record<string, unknown> | null } | null> {
    try {
      const response: AxiosResponse = await api.get('/keystone-query/me');
      return response.data;
    } catch {
      return null;
    }
  }

  static async fetchKeystone1099Types(): Promise<Array<{ code: string; desc: string }>> {
    try {
      const response: AxiosResponse = await api.get('/keystone-query/1099-types');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (response.data?.data || []).map((r: any) => ({
        code: String(r?.CODE ?? ''),
        desc: String(r?.DESC ?? ''),
      })).filter((r: { code: string }) => r.code);
    } catch {
      return [];
    }
  }
}

export default OCRApiService;
